import { useMemo, useState } from "react";

import { Input } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import Tag, { ProductivityTag } from "@/components/ui/Tag.jsx";
import { matches, resolve, validatePattern } from "@/lib/productivityRules.js";

/*
 * "Would this rule catch that session?"
 *
 * Runs the SAME matching the server does — see lib/productivityRules.js. Two
 * things it says out loud, because both surprise people and both silently
 * mis-classify everything when assumed wrong:
 *
 *   a plain pattern is an EXACT match, not "contains"
 *   matching is case-sensitive, regex included
 *
 * It also resolves against the other rules, because "my pattern matches" is not
 * the question — a domain rule outranks an application one, so a rule can be
 * perfectly correct and still never be the one that decides.
 */
export default function PatternTester({ draft, rules = [], teamId = null }) {
    const [sample, setSample] = useState("");

    const validity = validatePattern(draft.pattern, draft.isRegex);
    const hit = useMemo(() => matches(draft, sample), [draft, sample]);

    // What the organization would actually record, this draft included.
    const outcome = useMemo(() => {
        if (!sample) return null;
        const others = rules.filter((r) => r._id !== draft._id);
        return resolve([...others, draft], sample, teamId);
    }, [rules, draft, sample, teamId]);

    const shadowed = outcome && hit && outcome.rule && outcome.rule._id !== draft._id;

    return (
        <div className="rounded-lg border border-slate-200 p-3.5 dark:border-slate-800">
            <div className="mb-2 flex items-center gap-2">
                <p className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                    Test this pattern
                </p>
                <span className="ml-auto">
                    {!draft.pattern ? (
                        <Tag tone="slate">No pattern</Tag>
                    ) : !validity.ok ? (
                        <Tag tone="rose" data-test-result="invalid">
                            Invalid regex
                        </Tag>
                    ) : !sample ? (
                        <Tag tone="brand" data-test-result="ready">
                            {draft.isRegex ? "Valid regex" : "Exact match"}
                        </Tag>
                    ) : hit ? (
                        <Tag tone="emerald" data-test-result="match">
                            Match
                        </Tag>
                    ) : (
                        <Tag tone="rose" data-test-result="no-match">
                            No match
                        </Tag>
                    )}
                </span>
            </div>

            <Input
                value={sample}
                onChange={(e) => setSample(e.target.value)}
                placeholder={
                    draft.type === "application"
                        ? "Visual Studio Code"
                        : draft.type === "domain"
                          ? "youtube.com"
                          : "https://youtube.com/watch?v=…"
                }
                aria-label="Sample to test against"
                data-test-sample
                className="font-mono"
            />

            {!validity.ok && draft.pattern ? (
                <p className="mt-2 text-[12px] text-rose-600 dark:text-rose-400" data-test-hint>
                    {validity.message} — a pattern that cannot compile matches nothing at all, and
                    the rule fails silently.
                </p>
            ) : (
                <p className="mt-2 text-[12px] text-slate-500 dark:text-slate-400" data-test-hint>
                    {draft.isRegex
                        ? "Regular expression, case-sensitive and unanchored — it matches anywhere in the value."
                        : "Exact match, case-sensitive. The whole value must equal the pattern; this is not a “contains” search."}
                </p>
            )}

            {sample && validity.ok && (
                <div className="mt-2.5 space-y-2">
                    {hit ? (
                        <p className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
                            <span className="text-slate-500 dark:text-slate-400">
                                This rule would classify it as
                            </span>
                            <ProductivityTag status={draft.globalStatus} />
                        </p>
                    ) : (
                        <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
                            This rule would not catch it.
                        </p>
                    )}

                    {shadowed && (
                        <Callout tone="warning" data-shadowed>
                            <strong className="font-semibold">{outcome.rule.name}</strong> would
                            decide instead — {outcome.rule.type} rules are checked before{" "}
                            {draft.type} ones, and it also matches. The session would be recorded as{" "}
                            <strong className="font-semibold">{outcome.status}</strong>.
                        </Callout>
                    )}

                    {!hit && outcome?.rule && (
                        <p className="text-[12px] text-slate-400 dark:text-slate-500">
                            Another rule ({outcome.rule.name}) catches it and records it as{" "}
                            {outcome.status}.
                        </p>
                    )}

                    {!hit && !outcome?.rule && (
                        <p className="text-[12px] text-slate-400 dark:text-slate-500">
                            Nothing else catches it either — it would fall through to Unrated.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
